import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMutation, useQuery } from '@apollo/client';

import { FindOrCreateReferralCodeDocument, GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import type {
  DutchieReferralConfig,
  FindOrCreateReferralCodeMutation,
  GetDutchiePosLoyaltyConfigQuery,
  RewardsActivity,
  RewardV2,
} from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import useErnie from 'shared/hooks/use-ernie';
import { removeTypename } from 'shared/helpers/utils';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { detectLoyaltyIntegration, Integrations } from 'src/components/loyalty/use-loyalty';

type DposLoyaltyContextValue = {
  // Rewards
  rewards: RewardV2[];
  rewardsActivity: RewardsActivity[];
  isLoyaltyMember: boolean;
  isRevokedFromReferralProgram: boolean;
  // Config
  dposConfigData: GetDutchiePosLoyaltyConfigQuery['getDutchiePosLoyaltyConfig'] | null | undefined;
  loyaltyRequireOptIn: boolean;
  referralConfig: DutchieReferralConfig | null | undefined;
  // Referral code
  referral: FindOrCreateReferralCodeMutation['findOrCreateReferralCode'] | null | undefined;
  // Loading
  loading: boolean;
  // Display flags
  shouldDisplayLoyaltySection: boolean;
  shouldDisplayReferralsSection: boolean;
};

const defaultValue: DposLoyaltyContextValue = {
  rewards: [],
  rewardsActivity: [],
  isLoyaltyMember: false,
  isRevokedFromReferralProgram: false,
  dposConfigData: undefined,
  loyaltyRequireOptIn: false,
  referralConfig: undefined,
  referral: undefined,
  loading: false,
  shouldDisplayLoyaltySection: false,
  shouldDisplayReferralsSection: false,
};

const DposLoyaltyContext = createContext<DposLoyaltyContextValue>(defaultValue);

export const DposLoyaltyProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const { dispensary } = useDispensary();
  const isKioskProEnabled = useKioskPro();
  const flags = useFlags();
  const User = useUser();
  const showErnie = useErnie();
  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);
  const customerId = User?.id;

  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const isDutchiepos = loyaltyProgram === Integrations.dutchiepos;
  const isSkipSMSSendingEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const isShowLoyaltyFlagEnabled = (dispensary?.storeSettings?.showLoyaltyRewards ?? false) || !!isKioskProEnabled;
  const isAlpineIQEnabled = dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled ?? false;
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;
  const isSpringbigEnabled = dispensary?.consumerDispensaryIntegrations?.springbig?.enabled;
  const shouldDisplayLoyaltySection = !!(
    isShowLoyaltyFlagEnabled && [isAlpineIQEnabled, isDutchiePOSLoyaltyEnabled, isSpringbigEnabled].some(Boolean)
  );

  const { data, loading } = useGetRewards({
    dispensaryId: dispensary?.id,
    birthday,
    pin: isSkipSMSSendingEnabled && isAlpineIQEnabled ? 'skip' : '',
    phone,
    skip: (isSkipSMSSendingEnabled && isAlpineIQEnabled) || !isDutchiepos || !customerId || !phone,
    customerId,
  });

  const { isLoyaltyMember: rawIsLoyaltyMember, isRevokedFromReferralProgram: rawIsRevokedFromReferralProgram } =
    data?.getRewardsV2 ?? {};
  const isLoyaltyMember = rawIsLoyaltyMember ?? false;
  const isRevokedFromReferralProgram = rawIsRevokedFromReferralProgram ?? false;

  // Memoized on the stable Apollo array reference so downstream consumers don't re-render when data is unchanged.
  const rewards = useMemo(
    () => (data?.getRewardsV2?.rewards ?? []).filter((r) => r !== null).map((r) => removeTypename(r)) as RewardV2[],
    [data?.getRewardsV2?.rewards]
  );

  const rewardsActivity = useMemo(
    () =>
      (data?.getRewardsV2?.rewardsActivity ?? [])
        .filter((ra) => ra !== null)
        .map((ra) => removeTypename(ra)) as RewardsActivity[],
    [data?.getRewardsV2?.rewardsActivity]
  );

  const { data: configQueryData } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: { dispensaryId: dispensary?.id ?? '' },
    skip: !isDutchiepos,
  });

  const dposConfigData = configQueryData?.getDutchiePosLoyaltyConfig;
  const loyaltyRequireOptIn = dposConfigData?.loyaltyRequireOptIn ?? false;
  const referralConfig = dposConfigData?.referralConfig;

  const shouldDisplayReferralsSection = !!(
    isShowLoyaltyFlagEnabled &&
    !!isDutchiePOSLoyaltyEnabled &&
    !!referralConfig?.isEnabled &&
    !isRevokedFromReferralProgram
  );

  const [findOrCreateReferralCodeMutation, { data: findOrCreateReferralCodeData }] = useMutation(
    FindOrCreateReferralCodeDocument
  );

  useEffect(() => {
    if (
      isDutchiepos &&
      customerId &&
      dispensary?.id &&
      data?.getRewardsV2?.posUniqueId &&
      !isRevokedFromReferralProgram
    ) {
      const fetchReferralData = async (): Promise<void> => {
        try {
          await findOrCreateReferralCodeMutation({
            variables: {
              dispensaryId: dispensary.id,
              lspName: dispensary.dposLspName,
              senderUserId: customerId,
              posUniqueId: data.getRewardsV2?.posUniqueId,
            },
          });
        } catch {
          showErnie('Error fetching referral data. Please try again.', 'danger');
        }
      };
      void fetchReferralData();
    }
  }, [
    isDutchiepos,
    dispensary?.dposLspName,
    dispensary?.id,
    customerId,
    findOrCreateReferralCodeMutation,
    data?.getRewardsV2?.posUniqueId,
    showErnie,
    isRevokedFromReferralProgram,
  ]);

  const contextValue = useMemo(
    () => ({
      rewards,
      rewardsActivity,
      isLoyaltyMember,
      isRevokedFromReferralProgram,
      dposConfigData,
      loyaltyRequireOptIn,
      referralConfig,
      referral: findOrCreateReferralCodeData?.findOrCreateReferralCode,
      loading,
      shouldDisplayLoyaltySection,
      shouldDisplayReferralsSection,
    }),
    [
      rewards,
      rewardsActivity,
      isLoyaltyMember,
      isRevokedFromReferralProgram,
      dposConfigData,
      loyaltyRequireOptIn,
      referralConfig,
      findOrCreateReferralCodeData,
      loading,
      shouldDisplayLoyaltySection,
      shouldDisplayReferralsSection,
    ]
  );

  return <DposLoyaltyContext.Provider value={contextValue}>{children}</DposLoyaltyContext.Provider>;
};

export const useDposLoyalty = (): DposLoyaltyContextValue => useContext(DposLoyaltyContext);
